import { forwardRef } from 'react';
import type { RemixiconComponentType } from '@remixicon/react';
import { Theme } from '@mui/material';
import { SxProps } from '@mui/system';
import { DesignSystemColorModeFlattened } from '../../theme/design-system/types';
import { RemixIcon } from '../Icon/RemixIcon';
import { DesignSystemLeadItemSize } from './LeadItem.styleUtils';
import { useSlotContext } from '@upheal/design-system/utils/useSlotContext';
import {
    FeatureIcon,
    FeatureIconProps as BaseFeatureIconProps,
    FeatureIconStyle,
} from '../FeatureIcon/FeatureIcon';

export type { FeatureIconStyle };

export type FeatureIconProps = BaseFeatureIconProps & {
    type: 'feature-icon';
};

export interface BaseLeadItemProps {
    /**
     * Size of the icon
     * @default 'md'
     */
    size?: DesignSystemLeadItemSize;

    /**
     * Custom styles
     */
    sx?: SxProps<Theme>;

    /**
     * Can be used to opt out from usage of `leadItem` slot
     */
    slot?: null;
}

export interface IconProps extends BaseLeadItemProps {
    type: 'icon';
    icon: RemixiconComponentType;
    color?: DesignSystemColorModeFlattened;
}

export type LeadItemProps = FeatureIconProps | IconProps;

export const LeadItem = forwardRef<HTMLDivElement, LeadItemProps>(
    (props, ref) => {
        const slot = props.slot === null ? undefined : 'leadItem';
        const mergedSlotProps = useSlotContext(props, slot);
        const { size = 'md', sx } = mergedSlotProps;

        if (props.type === 'feature-icon') {
            const { type: _, slot: __, ...featureIconProps } = props;

            return (
                <FeatureIcon
                    ref={ref}
                    {...featureIconProps}
                    size={size}
                    sx={sx}
                />
            );
        }

        if (props.type === 'icon') {
            const { icon: Icon, color } = props;

            return (
                <RemixIcon
                    ref={ref}
                    icon={Icon}
                    size={size}
                    sx={sx}
                    color={color}
                />
            );
        }
    },
);
